import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { AccessProfileSelect } from './components/AccessProfileSelect';
import { InviteSuccess } from './components/InviteSuccess';
import { LinkedBrandsField } from './components/LinkedBrandsField';
import { UserTypeToggle } from './components/UserTypeToggle';
import { WhatsAppField } from './components/WhatsAppField';
import { useNewUserForm } from './hooks/useNewUserForm';

type NewUserProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export const NewUser = ({ isOpen, onClose, onSuccess }: NewUserProps) => {
  const {
    form,
    accountType,
    partnerIds,
    allPartners,
    selectedRole,
    partners,
    assignableRoles,
    canCreateCollaborator,
    canSelectAllBrands,
    typeHint,
    isSubmitting,
    inviteUrl,
    togglePartner,
    toggleAllPartners,
    submit,
  } = useNewUserForm({ isOpen, onSuccess, onClose });

  const {
    register,
    setValue,
    formState: { errors },
  } = form;

  const handleTypeChange = (value: 'COLLABORATOR' | 'CLIENT') => {
    setValue('accountType', value);
    setValue('role', '');
    setValue('partnerIds', []);
    setValue('allPartners', false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogPortal>
        <DialogOverlay className="bg-black/40 backdrop-blur-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:duration-300 data-[state=closed]:duration-200" />
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex h-[100dvh] w-full max-w-[min(420px,100vw)] min-w-0 flex-col overflow-hidden border-l bg-white shadow-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right-full data-[state=closed]:slide-out-to-right-full data-[state=open]:duration-300 data-[state=closed]:duration-300 data-[state=open]:ease-out data-[state=closed]:ease-in"
        >
          <div className="flex shrink-0 items-center justify-between px-6 py-4">
            <DialogTitle className="text-lg font-semibold text-gray-900">Novo usuário</DialogTitle>
            <button
              type="button"
              onClick={onClose}
              className="rounded-sm p-1 text-gray-700 opacity-70 hover:opacity-100"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </div>
          {inviteUrl ? (
            <InviteSuccess inviteUrl={inviteUrl} onFinish={onClose} />
          ) : (
            <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 space-y-5 overflow-y-auto px-6 pb-5">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-900">Tipo de usuário</p>
                  <UserTypeToggle
                    value={accountType}
                    onChange={handleTypeChange}
                    disabled={!canCreateCollaborator}
                  />
                  <p className="text-sm text-gray-500">{typeHint}</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-900">
                    Nome completo <span className="text-red-500">*</span>
                  </label>
                  <Input
                    {...register('name')}
                    placeholder="Digite o nome completo"
                    className={errors.name ? 'border-red-500' : ''}
                    error={errors.name?.message}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-900">
                    E-mail <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    {...register('email')}
                    placeholder="Digite o e-mail"
                    className={errors.email ? 'border-red-500' : ''}
                    error={errors.email?.message}
                  />
                </div>
                <WhatsAppField {...register('whatsapp')} />
                {accountType === 'COLLABORATOR' ? (
                  <AccessProfileSelect
                    value={selectedRole}
                    options={assignableRoles}
                    error={errors.role?.message}
                    onChange={(role) => setValue('role', role, { shouldValidate: true })}
                  />
                ) : null}
                <div className="space-y-3 border-t pt-5">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Marcas vinculadas</p>
                    <p className="text-sm text-gray-500">
                      {accountType === 'CLIENT'
                        ? 'Selecione a marca'
                        : 'Selecione quais marcas o colaborador deverá ser vinculado.'}
                    </p>
                  </div>
                  <LinkedBrandsField
                    partners={partners}
                    selectedIds={partnerIds}
                    allSelected={allPartners}
                    multiple={accountType === 'COLLABORATOR'}
                    allowSelectAll={canSelectAllBrands}
                    error={errors.partnerIds?.message}
                    onToggle={togglePartner}
                    onToggleAll={toggleAllPartners}
                  />
                </div>
              </div>
              <div className="flex shrink-0 justify-end gap-2 border-t px-6 py-4">
                <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button type="submit" variant="dark" disabled={isSubmitting} className="min-w-[96px] gap-2">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Salvar
                </Button>
              </div>
            </form>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};
